import type { ColDef } from "ag-grid-community"

import type { DynamicColumnId } from "~/components/planning/ProductTable/utils/columnUtils"
import { createStringColumn } from "~/utils/columnTemplates"
import { formatVariantId } from "~/utils/helpers"
import { type GroupSorting, SIZE_GROUP_SORTING } from "~/utils/productTableUtils"

// True when a group row sits at the variant level — the deepest grouping tier. Grouping by
// variant puts it at the top (index 0); any other dimension nests it one level down (index 1).
// Used to pick the variant cell renderer, and to cap editing at the variant level in the
// Fashion buy table (SLIM-515). The NOOS buy table edits at any group level (SLIM-540).
export function isVariantGroupLevel(
  level: number | null | undefined,
  groupedByVariant: boolean,
): boolean {
  return groupedByVariant ? level === 0 : level === 1
}

export function createVariantGroupingColumn<T>(opts: {
  colId: string
  rowGroup?: boolean
  rowGroupIndex?: number
  field?: string
  headerName?: string
  sorting?: GroupSorting
  valueGetter?: ColDef<T>["valueGetter"]
}): ColDef<T> {
  return createStringColumn<T>({
    colId: opts.colId as DynamicColumnId,
    field: (opts.field ?? "variantId") as never,
    ...(opts.sorting ?? SIZE_GROUP_SORTING),
    headerName: opts.headerName ?? "Variant",
    rowGroup: opts.rowGroup ?? true,
    rowGroupIndex: opts.rowGroupIndex,
    hide: true,
    aggFunc: "unique",
    valueGetter:
      opts.valueGetter ??
      ((params) => {
        const data = params.data as { variantId?: string } | undefined
        if (!data?.variantId) return ""
        return formatVariantId(data.variantId)
      }),
  })
}

// Builds the row-grouping columns for the buy tables. Grouping by Variant stays a single
// level (VARIANT → SKU). Any other dimension nests a Variant level beneath it
// (e.g. PRODUCT → VARIANT → SKU) so users can still drill down to individual variants and
// their SKUs (SLIM-515). Both columns are hidden; AG-Grid renders them via the auto group
// column. rowGroupIndex pins the dimension above the variant level.
export function createGroupingColumns<T>(opts: {
  groupColId: string
  variantGroupColId: string
  headerName: string
  sorting?: GroupSorting
  includeVariantLevel: boolean
  groupValueGetter: ColDef<T>["valueGetter"]
}): ColDef<T>[] {
  const dimensionColumn = createVariantGroupingColumn<T>({
    colId: opts.groupColId,
    headerName: opts.headerName,
    sorting: opts.sorting,
    rowGroupIndex: 0,
    valueGetter: opts.groupValueGetter,
  })

  if (!opts.includeVariantLevel) return [dimensionColumn]

  const variantColumn = createVariantGroupingColumn<T>({
    colId: opts.variantGroupColId,
    headerName: "Variant",
    sorting: SIZE_GROUP_SORTING,
    rowGroupIndex: 1,
  })

  return [dimensionColumn, variantColumn]
}
